<script setup lang="ts">
import { computed } from 'vue';
import VotePositiveStaff from '@/assets/icons/votes/vote-positive-filled-alternate.svg?component';
import VotePositiveClient from '@/assets/icons/votes/vote-positive-filled.svg?component';
import VotePositiveCombo from '@/assets/icons/votes/vote-positive-filled-combo.svg?component';
import VotePositiveMaster from '@/assets/icons/votes/vote-positive-purple-filled.svg?component';
import VotePositiveOutline from '@/assets/icons/votes/vote-positive-outline.svg?component';
import VoteNegativeStaff from '@/assets/icons/votes/vote-negative-filled-alternate.svg?component';
import VoteNegativeClient from '@/assets/icons/votes/vote-negative-filled.svg?component';
import VoteNegativeCombo from '@/assets/icons/votes/vote-negative-filled-combo.svg?component';
import VoteNegativeMaster from '@/assets/icons/votes/vote-negative-purple-filled.svg?component';
import VoteNegativeOutline from '@/assets/icons/votes/vote-negative-outline.svg?component';
import CommentStaffFilled from '@/assets/icons/votes/comment-green-filled.svg?component';
import CommentStaffOutline from '@/assets/icons/votes/comment-green-outline.svg?component';
import CommentClientFilled from '@/assets/icons/votes/comment-orange-filled.svg?component';
import CommentClientOutline from '@/assets/icons/votes/comment-orange-outline.svg?component';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import TrackVotesPopover from '@/components/TrackVotes/TrackVotesPopover.vue';
import {
  commentCount,
  hasVoteIndicator,
  excludesTrack,
  isOwnVote,
  voteAriaLabel,
  voteGlyphFor,
  voteVariant,
} from '@/components/TrackVotes/trackVoteDisplay';
import type { TrackVoteSummary } from '@/composables/api/usePlaylistSwitchingApi';

/**
 * The per-row vote indicator: a small glyph (love / hate / comment) whose COLOUR
 * says who voted — green staff, orange client, a split glyph when both did, and
 * purple when the deciding vote came from the master zone rather than this one.
 *
 * Filled-versus-outline is NOT "is this mine" for hearts and crosses: a heart or
 * a cross is always filled, because a deciding vote always has one of the four
 * colours above. The outline pair exists for the no-vote case. Only the comment
 * bubble uses fill to mean "this comment is mine" ({@link isOwnVote}).
 *
 * A comment count chip keeps a comment-only track from reading as un-voted.
 * Clicking opens the detail popover. Renders nothing when there are no visible
 * votes.
 *
 * The whole visual grammar lives in {@link trackVoteDisplay} so it's shared and
 * unit-tested; this component only maps that grammar to icons and classes.
 */
const props = withDefaults(
  defineProps<{
    votes?: TrackVoteSummary | null;
    /** Hides the count chip / excluded marker for the tightest (compact) rows. */
    dense?: boolean;
  }>(),
  { votes: null, dense: false },
);

const shows = computed(() => hasVoteIndicator(props.votes));
const glyph = computed(() => voteGlyphFor(props.votes));
const own = computed(() => isOwnVote(props.votes));
const comments = computed(() => commentCount(props.votes));
const excluded = computed(() => excludesTrack(props.votes));
const ariaLabel = computed(() => voteAriaLabel(props.votes));

/**
 * The heart / X / bubble the macOS app draws, in its own colours — these are
 * the original vector files, so the marks a playlist manager already knows
 * mean the same thing here. Variant chosen by {@link voteVariant}.
 */
const HEART = {
  staff: VotePositiveStaff,
  client: VotePositiveClient,
  combo: VotePositiveCombo,
  master: VotePositiveMaster,
  none: VotePositiveOutline,
} as const;

const CROSS = {
  staff: VoteNegativeStaff,
  client: VoteNegativeClient,
  combo: VoteNegativeCombo,
  master: VoteNegativeMaster,
  none: VoteNegativeOutline,
} as const;

const variant = computed(() => voteVariant(props.votes));

const icon = computed(() => {
  switch (glyph.value) {
    case 'love':
      return HEART[variant.value];
    case 'hate':
      return CROSS[variant.value];
    case 'comment':
      // Comments have only a green and an orange bubble, and filled-vs-outline
      // is reserved for "is this comment mine", so a master-origin or combo
      // comment falls back to its side's colour.
      if (variant.value === 'client') {
        return own.value ? CommentClientFilled : CommentClientOutline;
      }
      return own.value ? CommentStaffFilled : CommentStaffOutline;
    default:
      return null;
  }
});
</script>

<template>
  <Popover v-if="shows">
    <PopoverTrigger as-child>
      <button
        type="button"
        class="hover:bg-muted focus-visible:ring-ring inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5 transition-colors focus-visible:ring-2 focus-visible:outline-none"
        :aria-label="ariaLabel"
        :title="ariaLabel"
        :data-testid="`track-vote-indicator`"
      >
        <component :is="icon" v-if="icon" class="h-4 w-4 shrink-0" aria-hidden="true" />
        <span
          v-if="!dense && comments > 0"
          class="text-muted-foreground text-[11px] leading-none tabular-nums"
          data-testid="track-vote-comment-count"
          >{{ comments }}</span
        >
        <span
          v-if="!dense && excluded"
          class="bg-destructive/15 text-destructive rounded px-1 text-[10px] leading-none font-medium"
          data-testid="track-vote-excluded-badge"
          >✕ list</span
        >
      </button>
    </PopoverTrigger>
    <PopoverContent
      align="end"
      side="bottom"
      class="w-80 max-w-[90vw] p-3"
      data-testid="track-vote-popover-content"
    >
      <TrackVotesPopover v-if="votes" :summary="votes" />
    </PopoverContent>
  </Popover>
</template>
